/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * GA GỘP — một cái van đứng giữa "khung realtime về" và "đi hỏi lại máy chủ".
 *
 * ➕ `GD-10c` 14/09/2026 — TÁCH RA TỆP RIÊNG, và lý do KHÔNG phải là cho gọn.
 *
 * 🔴 `[114]` phải **CHẠY** cái ga này với một đồng hồ giả, không được đọc mặt chữ của nó. Ba
 *   lượt vừa rồi đã dạy đúng điều đó ba lần:
 *     · `[113]a` đo mặt chữ bằng regex ⇒ không bắt được `HangO` (props hai trường có dấu `;`);
 *     · `[113]b` đo mặt chữ ⇒ không bắt được một `return;` trần làm ga mất đuôi;
 *     · `GD-10b` đo mặt chữ ⇒ ga vẫn nhân đôi ở máy lee, và tôi phải đi tìm bằng thí nghiệm.
 *   Một phép ĐỌC mã không bao giờ trả lời được câu *"cái ga này nổ mấy lần trong 36 giây"*.
 * ⛔ Mà `verify-protocol.ts` chạy trong Node thì không nạp nổi `queries.ts` (tệp ấy kéo theo
 *   React và `@tanstack/react-query`). ⇒ Ga phải nằm ở một tệp **KHÔNG nhập gì của trình
 *   duyệt** — y hệt `lib/csv.ts` và `lib/canhbao.ts`, hai tệp bộ kiểm đã nạp thẳng từ lâu.
 * ⚠ Và vì thế tệp này TUYỆT ĐỐI không được `import` thêm thứ gì. Thêm một dòng nhập React vào
 *   đây là làm câm `[114]` — mà `[114]` là phép duy nhất đo được HÀNH VI của ga.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * Đồng hồ của ga — tách ra để `[114]` cắm được một cái đồng hồ ẢO vào.
 *
 * ⛔ ĐÂY KHÔNG PHẢI "MỘT CỬA CHO BỘ KIỂM". Nó là cách DUY NHẤT để hỏi cái ga một câu hỏi về
 *   THỜI GIAN mà không phải ngồi chờ 36 giây thật, và để dựng được ca *"timer nổ TRỄ"* — ca
 *   mà máy của tôi không bao giờ tự sinh ra, và máy lee thì sinh ra mỗi ngày.
 */
export interface DongHoGa {
  now(): number;
  hen(f: () => void, ms: number): unknown;
  huy(t: unknown): void;
}

const DONG_HO_THAT: DongHoGa = {
  now: () => Date.now(),
  hen: (f, ms) => setTimeout(f, ms),
  huy: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
};

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ĐẦU + ĐUÔI: rảnh thì chạy NGAY; dồn dập thì gộp đúng MỘT lượt vào cuối cửa sổ.
 *
 * ⚠ Vì sao cả hai vế:
 *   · chỉ ĐẦU (chạy ngay rồi câm N ms) ⇒ khung đến sau trong cửa sổ bị **vứt hẳn**, và dữ
 *     liệu đứng yên tới N giây mà không ai biết — một sự cũ kỹ IM LẶNG;
 *   · chỉ ĐUÔI (luôn chờ hết N ms) ⇒ một khung lẻ loi cũng phải đợi, kể cả lúc hệ rảnh.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🚨 `GD-10c` — THỨ TỰ HAI CÂU HỎI TRONG `goi()` LÀ CẢ CÁI SỬA. dev_device đo ở máy thật
 *   (14/09 15:40): sau `GD-10b` ga VẪN ra hai lượt mỗi cửa sổ, cách nhau 0,2–0,5 s — và lần
 *   này KHÔNG phải hai bộ nghe (máy lee không có câu cảnh báo "2 bản").
 *
 * 🔴 GỐC: bản cũ hỏi *"đã quá hạn chưa"* TRƯỚC *"có hẹn nào đang treo không"*. Khi một
 *   `setTimeout` nổ TRỄ (máy bận, tab nền, GC), có một khoảng mà **cả hai đường đều mở**:
 *       · cái hẹn đã tới giờ nhưng trình duyệt chưa gọi nó;
 *       · một khung mới tới, `conLai <= 0` ⇒ chạy NGAY, `return` — **và bỏ cái hẹn nằm đó**.
 *     Vài trăm mili-giây sau, cái hẹn nổ và chạy LƯỢT THỨ HAI.
 *   Đó đúng là cặp lee đo được, và đúng lý do tôi không thấy: hộp cát của tôi timer không trễ.
 *
 * ⇒ HỎI `hen` TRƯỚC. Có hẹn treo nghĩa là **đã có người xếp hàng cho cửa sổ này** — mọi lời
 *   gọi khác chỉ được GỘP vào nó, không được mở một đường chạy song song. Đường chạy-ngay chỉ
 *   còn mở khi KHÔNG có hẹn nào, tức là không thể đua với ai.
 * ⛔ KHÔNG sửa bằng cách `huy(hen)` rồi chạy ngay: thế là cho cái khung mới **cướp** chỗ của
 *   cái hẹn, và mốc `chayLucMs` nhảy tới sớm hơn ⇒ cửa sổ trượt dần, trần hết đúng.
 * ⚠ `[114]` chạy chính hàm này với timer giả TRỄ 500 ms để ghim điều đó — không đọc mặt chữ.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function taoGa(msMin: number, dongHo: DongHoGa = DONG_HO_THAT) {
  let chayLucMs = 0;
  let hen: unknown = null;
  let viecChoSan: (() => void) | null = null;
  return {
    goi(lam: () => void) {
      /*
       * 🚨 VẾ NÀY PHẢI ĐỨNG ĐẦU — xem khối chú thích ngay trên. Đẩy nó xuống dưới `conLai` là
       *   mở lại đúng cửa sổ đua đã làm ga nhân đôi ở máy lee.
       */
      if (hen) { viecChoSan = lam; return; }
      const bayGio = dongHo.now();
      const conLai = msMin - (bayGio - chayLucMs);
      if (conLai <= 0) { chayLucMs = bayGio; lam(); return; }
      viecChoSan = lam;
      hen = dongHo.hen(() => {
        hen = null;
        chayLucMs = dongHo.now();
        const v = viecChoSan; viecChoSan = null;
        if (v) v();
      }, conLai);
    },
    /**
     * ⛔ Dọn hẹn còn treo. Một `setTimeout` nổ sau khi bộ nghe đã chết là một sợi dây không ai
     *   đo — vô hại hôm nay, và là thứ để lại về sau không giải thích nổi.
     */
    don() {
      if (hen) { dongHo.huy(hen); hen = null; }
      viecChoSan = null;
    },
  };
}
